import React from "react";
import { ResponsiveBlock } from "components/ResponsiveBlock";
import styled from "styled-components";
import { LazyImage } from "components/shared/LazyImage/LazyImage";
import { Button, ButtonLink } from "ui/molecules";
import { Text } from "ui/atoms";
import defaultTheme from "config/themes/post-rebrand/theme";
import { Heading } from "ui/atoms/typography/Heading";
import MailIcon from "svg/mail.svg";
import PhoneIcon from "svg/phone-empty-inside.svg";
import EclipseIcon from "svg/eclipse.svg";
import { Link } from "components/information-lister-pages/PlaceholderBlock/elements/About/elements/Link";
import { Screens } from "helpers/screens";
import { useMediaContext } from "lib/media-query";

const AboutContainer = styled.div`
    display: grid;
    border-radius: 3.5rem 0;
    background-color: ${({ theme }) => theme.colors.quarternary[700]};
    grid-template-rows: 1fr;

    @media (${Screens.get("lg")}) {
        grid-template-columns: 3fr 2fr;
    }
`;

const ContactsBlock = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
    gap: 2rem;
    padding: 1.31rem;

    @media (${Screens.get("lg")}) {
        padding: 3.5rem;
    }
`;

const ButtonDescriptionWrapper = styled.div`
    display: grid;
    grid-template-rows: 2fr 1fr;
    gap: 0.5rem;
    height: 100%;
`;

const CtaWrapper = styled.div`
    display: grid;
    align-items: center;
    justify-content: center;
    grid-template-columns: 1fr;

    gap: 1.25rem;

    @media (${Screens.get("lg")}) {
        grid-template-columns: repeat(2, 1fr);
    }
`;

const TitleWrapper = styled.div`
    display: flex;
    flex-direction: column;
    text-align: center;
    gap: 1.5rem;

    @media (${Screens.get("lg")}) {
        text-align: left;
    }
`;

const StyledButton = styled(Button)`
    padding: 0.96875rem 1.875rem;
    gap: 0.625rem;
    height: 100%;
    width: 100%;

    svg {
        width: 1.125rem;
        height: 1.125rem;
        flex-shrink: 0;
    }
`;

const StyledTimeText = styled(Text)`
    display: flex;
    align-items: center;
    gap: 0.62rem;
    align-self: center;
    justify-content: center;

    @media (${Screens.get("lg")}) {
        justify-content: flex-start;
    }
`;

const ImageCotainer = styled.div`
    display: flex;
    flex-direction: column;
    height: 100%;
    width: 100%;
    grid-column: 1/2;

    @media (${Screens.get("lg")}) {
        align-items: end;
        grid-column: 2/4;
    }
`;

const StyledImage = styled(LazyImage)`
    background-color: transparent;

    @media (${Screens.get("lg")}) {
        width: 30rem;
    }
`;

export const TextDescription = styled.p`
    color: ${({ theme }) => theme.colors.secondary[900]};
`;

export function About() {
    const mediaContext = useMediaContext();
    const isMobilePhone = mediaContext.isMobile || mediaContext.isMobilePlus;

    return (
        <ResponsiveBlock>
            <AboutContainer>
                <ContactsBlock>
                    <TitleWrapper>
                        <Heading
                            style={{
                                fontFamily: "'Roboto Slab', serif"
                            }}
                            as="h2"
                            size="xl"
                            textColor={defaultTheme.colors.secondary[900]}
                        >
                            Heb je hulp nodig?
                        </Heading>
                        <TextDescription>
                            Voor vragen, opmerkingen, tips en adviezen mag je ons altijd benaderen.
                            Ons team van experts staat voor je klaar.
                        </TextDescription>
                    </TitleWrapper>
                    <CtaWrapper>
                        <ButtonDescriptionWrapper>
                            <ButtonLink variant="primary" href="tel:0887300100">
                                <PhoneIcon />
                                <Text as="span" textColor={defaultTheme.colors.white}>
                                    Bel ons
                                </Text>
                            </ButtonLink>
                            <StyledTimeText as="span" textColor="black">
                                <EclipseIcon />
                                Ma - vr 8:00 tot 17:00
                            </StyledTimeText>
                        </ButtonDescriptionWrapper>
                        <ButtonDescriptionWrapper>
                            <Link href="mailto:info@autotrack.nl">
                                <StyledButton variant="secondary">
                                    <MailIcon />
                                    Mail ons
                                </StyledButton>
                            </Link>
                        </ButtonDescriptionWrapper>
                    </CtaWrapper>
                </ContactsBlock>
                <ImageCotainer>
                    <div style={{ height: "100%" }} />
                    <StyledImage
                        src={
                            isMobilePhone
                                ? "/static/images/contact-mobile.png"
                                : "/static/images/contact.png"
                        }
                        alt="at-team-contacts"
                    />
                </ImageCotainer>
            </AboutContainer>
        </ResponsiveBlock>
    );
}
